import { motion } from "framer-motion";
import {
  ShieldCheck,
  FileCheck,
  AlertTriangle,
  Radio,
  Cloud,
  Bot,
  Users,
} from "lucide-react";

const SERVICES = [
  {
    icon: ShieldCheck,
    title: "Security & Compliance Foundations",
    desc: "ISO 27001, SOC 2, PCI DSS, HIPAA framework implementation. Security policy drafting, audit preparation, and governance program development.",
  },
  {
    icon: FileCheck,
    title: "Customer & Go-to-Market Security",
    desc: "Security posture documentation, customer questionnaires, pen-test summaries, and trust centre advisory for enterprise sales cycles.",
  },
  {
    icon: AlertTriangle,
    title: "Risk & Operational Security",
    desc: "Risk assessments, vendor security evaluations, VAPT coordination, incident response planning, and business continuity advisory.",
  },
  {
    icon: Radio,
    title: "SecOps & Security Operations",
    desc: "SIEM use-case development, log management strategy, incident triage workflows, threat detection, and security metrics dashboards.",
  },
  {
    icon: Cloud,
    title: "DevSecOps & Cloud Security",
    desc: "Secure SDLC, CI/CD security controls, SAST/DAST integration, container security, and cloud architecture reviews across AWS, Azure, GCP.",
  },
  {
    icon: Bot,
    title: "AI Security & Emerging Tech Risk",
    desc: "AI governance advisory, LLM risk assessments, secure GenAI adoption, AI data privacy reviews, and threat modelling for AI-enabled products.",
  },
  {
    icon: Users,
    title: "Strategic & Fractional Leadership",
    desc: "Virtual CISO support, security maturity assessments, executive reporting, budget prioritisation, and advisory for fundraising and partnerships.",
  },
];

function ServiceCard({ service, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      data-testid={`service-card-${index}`}
      className="service-card bg-[#131316] relative group p-8 transition-all duration-300 hover:bg-[#1A1A1E]"
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
        e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
      }}
    >
      <div className="relative z-10">
        <div className="w-10 h-10 rounded-lg bg-[#B87333]/10 flex items-center justify-center mb-5 group-hover:bg-[#B87333]/20 transition-colors">
          <service.icon className="w-5 h-5 text-[#B87333]" />
        </div>
        <h3 className="text-lg font-medium text-white mb-3 tracking-tight">
          {service.title}
        </h3>
        <p className="text-sm text-zinc-500 leading-relaxed">
          {service.desc}
        </p>
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#0A0A0B]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E5A97A]/80 block mb-4">
            What We Do
          </span>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white">
            Comprehensive Security Services
          </h2>
        </motion.div>

        {/* Grid with 1px borders */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/10 p-[1px] border border-white/10 rounded-xl overflow-hidden">
          {SERVICES.map((service, i) => (
            <ServiceCard key={i} service={service} index={i} />
          ))}
          {/* Fill the last cell for even grid on desktop */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.56, duration: 0.5 }}
            className="bg-[#131316] relative group p-8 flex items-center justify-center lg:col-span-2"
          >
            <div className="text-center">
              <p className="text-zinc-500 text-sm mb-4">Need a tailored security approach?</p>
              <a
                href="#contact"
                data-testid="services-cta"
                className="inline-flex items-center gap-2 text-[#B87333] hover:text-[#E5A97A] text-sm font-medium transition-colors"
              >
                Let&apos;s discuss your requirements
                <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
